import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, ArrowDown, Target } from '@phosphor-icons/react';
import { gameSystems, getSymbol } from '../data/gameSystems';
import { useTranslation } from '../hooks/useTranslation';
import { ScrollArea } from './ui/scroll-area';

export const HistoryList = ({ transactions, systemId }) => {
  const { t, lang } = useTranslation();
  const system = gameSystems[systemId];
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'spend':
        return <ArrowDown size={18} weight="bold" className="text-red-500" />;
      case 'add':
        return <ArrowUp size={18} weight="bold" className="text-green-500" />;
      case 'goalDeposit':
        return <Target size={18} weight="bold" className="text-blue-500" />;
      case 'goalWithdraw':
        return <Target size={18} weight="bold" className="text-orange-500" />;
      default:
        return <ArrowUp size={18} weight="bold" className="text-green-500" />;
    }
  };
  
  const getTransactionBg = (type) => {
    switch (type) {
      case 'spend': return 'bg-red-500/10';
      case 'add': return 'bg-green-500/10';
      case 'goalDeposit': return 'bg-blue-500/10';
      case 'goalWithdraw': return 'bg-orange-500/10';
      default: return 'bg-green-500/10';
    }
  };
  
  const getAmountColor = (type) => {
    switch (type) {
      case 'spend':
      case 'goalDeposit':
        return 'text-red-500';
      default:
        return 'text-green-500';
    }
  };
  
  const getPrefix = (type) => {
    return (type === 'spend' || type === 'goalDeposit') ? '-' : '+';
  };
  
  if (!transactions || transactions.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--text-muted)]">{t('noTransactions')}</p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[320px]">
      <div className="space-y-2 pr-2">
        {transactions.map((tx, index) => {
          const currency = system.currencies.find(c => c.id === tx.currencyId);
          
          return (
            <motion.div
              key={tx.id}
              data-testid={`transaction-${tx.id}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-[var(--background)]"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.02 }}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getTransactionBg(tx.type)}`}>
                {getTransactionIcon(tx.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-base font-semibold ${getAmountColor(tx.type)}`}>
                    {getPrefix(tx.type)}{tx.amount} {currency ? getSymbol(currency, lang) : ''}
                  </span>
                  <span className="text-xs text-[var(--text-muted)] flex-shrink-0">
                    {formatDate(tx.timestamp)}
                  </span>
                </div>
                {tx.note && (
                  <p className="text-sm text-[var(--text-muted)] truncate mt-0.5">
                    {tx.note}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </ScrollArea>
  );
};
